/**
 * Font Size Context
 * Lesson text size (Text / Text+Audio). Persisted to account; applies only where lessonTypography is passed into FormattedTextBlock.
 */

import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';

export const LESSON_FONT_MULTIPLIER_STORAGE_KEY = 'learnerLessonFontMultiplier';

const MIN_FONT_SIZE = 0.35;
const MAX_FONT_SIZE = 3.0;
const DEFAULT_FONT_SIZE = 1.0;

function readStoredMultiplier(): number {
  try {
    const raw = localStorage.getItem(LESSON_FONT_MULTIPLIER_STORAGE_KEY);
    if (raw == null) return DEFAULT_FONT_SIZE;
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return DEFAULT_FONT_SIZE;
    return Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, n));
  } catch {
    return DEFAULT_FONT_SIZE;
  }
}

interface FontSizeContextType {
  currentMultiplier: number;
  setFontSize: (multiplier: number) => void;
  resetFontSize: () => void;
  isModified: boolean;
}

export const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

export const FontSizeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentMultiplier, setCurrentMultiplier] = useState(readStoredMultiplier);

  useEffect(() => {
    const onHydrated = (e: Event) => {
      const m = (e as CustomEvent<{ multiplier?: number }>).detail?.multiplier;
      if (typeof m === 'number' && Number.isFinite(m)) {
        setCurrentMultiplier(Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, m)));
      }
    };
    window.addEventListener('app:lessonTextMultiplierHydrated', onHydrated as EventListener);
    return () => window.removeEventListener('app:lessonTextMultiplierHydrated', onHydrated as EventListener);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LESSON_FONT_MULTIPLIER_STORAGE_KEY, String(currentMultiplier));
    } catch {
      /* ignore */
    }
  }, [currentMultiplier]);

  const setFontSize = useCallback((multiplier: number) => {
    const clamped = Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, multiplier));
    setCurrentMultiplier(clamped);
  }, []);

  const resetFontSize = useCallback(() => {
    setCurrentMultiplier(DEFAULT_FONT_SIZE);
  }, []);

  const isModified = Math.abs(currentMultiplier - DEFAULT_FONT_SIZE) > 0.001;

  return (
    <FontSizeContext.Provider
      value={{
        currentMultiplier,
        setFontSize,
        resetFontSize,
        isModified
      }}
    >
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => {
  const context = useContext(FontSizeContext);
  if (context === undefined) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
};
